import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";

// Image formats the backend (Pillow) can verify, and its 100 KB size limit.
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/bmp', 'image/webp'];
const MAX_IMAGE_SIZE_KB = 100;

export const IMAGE_UPLOAD_ACCEPT = ALLOWED_IMAGE_TYPES.join(',');

export function validateImageFile(file, label = 'Image') {
  if (!file) {
    return false;
  }
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    Swal.fire(
      'Invalid Image',
      `${label}: "${file.name}" is not a supported image. Please upload a PNG, JPG, GIF, BMP or WEBP file.`,
      'warning'
    );
    return false;
  }
  if (file.size > MAX_IMAGE_SIZE_KB * 1024) {
    Swal.fire(
      'Image Too Large',
      `${label}: "${file.name}" is ${Math.round(file.size / 1024)} KB. Maximum allowed size is ${MAX_IMAGE_SIZE_KB} KB.`,
      'warning'
    );
    return false;
  }
  return true;
}

export function displayError(errorMessage, title) {
  let message = 'An unexpected error occurred.';
  let code = '';
  let detail = [];

  if (typeof errorMessage === 'string' && errorMessage.trim()) {
    if (errorMessage.includes('<!DOCTYPE html>') || errorMessage.includes('<html')) {
      const titleMatch = errorMessage.match(/<title>([^<]+)<\/title>/i);
      const titleText = titleMatch?.[1] || '';
      if (/not found|404/i.test(titleText)) {
        message = 'The requested API endpoint was not found. Restart the Django server if this report was recently added.';
      } else {
        message = titleText || 'The server returned an HTML error page.';
      }
    } else {
      message = errorMessage;
    }
  } else if (errorMessage && typeof errorMessage === 'object') {
    if (Array.isArray(errorMessage.message)) {
      message = errorMessage.message.map((item) => String(item)).join('; ');
    } else if (errorMessage.message !== undefined && errorMessage.message !== null && String(errorMessage.message).trim()) {
      message = String(errorMessage.message);
    } else if (typeof errorMessage.detail === 'string' && errorMessage.detail.trim()) {
      message = errorMessage.detail;
    } else if (Array.isArray(errorMessage.detail)) {
      message = 'Validation failed.';
      detail = errorMessage.detail;
    } else if (errorMessage.detail && typeof errorMessage.detail === 'object') {
      message = Object.entries(errorMessage.detail)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    } else {
      try {
        const serialized = JSON.stringify(errorMessage);
        if (serialized && serialized !== '{}') {
          message = serialized;
        }
      } catch (e) {
        message = 'An unexpected error occurred.';
      }
    }

    if (errorMessage.code !== undefined && errorMessage.code !== null && errorMessage.code !== '') {
      code = errorMessage.code;
    } else if (errorMessage.status !== undefined && errorMessage.status !== null) {
      code = errorMessage.status;
    }

    if (Array.isArray(errorMessage.detail) && errorMessage.detail.length > 0) {
      detail = errorMessage.detail;
    }
  }

  let htmlContent = code
    ? `<b><i>${message} (Code: ${code})</i></b>`
    : `<b><i>${message}</i></b>`;

  if (detail.length > 0) {
    htmlContent += '<ul>';
    detail.forEach((detailItem) => {
      htmlContent += '<li>';
      if (typeof detailItem === 'object' && detailItem !== null) {
        Object.entries(detailItem).forEach(([key, value]) => {
          htmlContent += `<strong>${key}:</strong> ${value}<br>`;
        });
      } else {
        htmlContent += `${detailItem}`;
      }
      htmlContent += '</li>';
    });
    htmlContent += '</ul>';
  }

  Swal.fire({
    title: '<img src="/error-icon.png" width="110" height="60"/>' + title,
    html: htmlContent,
    customClass: {
      popup: 'swal-wide',
      icon: 'error',
    },
  });
}

export function getLoginErrorMessage(error, apiBaseUrl = '') {
  if (!error?.response) {
    if (error?.code === 'ERR_NETWORK' || String(error?.message || '').includes('Network Error')) {
      const serverUrl = apiBaseUrl || 'the API server';
      return `Cannot connect to ${serverUrl}. Start the backend server and try again.`;
    }
    return error?.message || 'Unable to reach server. Check that the backend is running.';
  }

  const status = error.response.status;
  const data = error.response.data;
  const detail = typeof data?.detail === 'string'
    ? data.detail
    : (typeof data?.message === 'string' ? data.message : '');

  if (status === 401) {
    if (detail.toLowerCase().includes('otp')) {
      return detail || 'Incorrect OTP. Enter the current code from your authenticator app.';
    }
    return detail || 'Invalid username or password.';
  }
  if (status === 403) {
    return detail || 'Access denied. Your account validity may have expired or your IP is not allowed.';
  }
  if (status === 400) {
    if (detail.toLowerCase().includes('latitude') || detail.toLowerCase().includes('location')) {
      return `${detail} Enable browser location permission and refresh the page.`;
    }
    return detail || 'Login request is incomplete. Check username and password.';
  }

  return detail || 'Login failed. Please try again.';
}

export function displayErrorToast(titleMessage) {
  const message = titleMessage == null ? 'An error occurred.' : String(titleMessage);
  const snackbar = document.getElementById("snackbar");
  const title = document.getElementById("snackbar-title");

  if (!snackbar || !title) {
    console.error(message);
    return;
  }

  const icon = document.getElementById("snackbar-icon");
  if (icon) {
    icon.src = "/error-icon.png";
    icon.height = '40';
    icon.width = '90';
  }

  title.innerText = message;
  snackbar.className = "show error";
  setTimeout(function () {
    snackbar.className = snackbar.className.replace("show", "");
  }, 3000);
}

export function displaySuccessToast(titleToast) {
  const message = titleToast == null ? 'Success.' : String(titleToast);
  const snackbar = document.getElementById("snackbar");
  const title = document.getElementById("snackbar-title");

  if (!snackbar || !title) {
    console.log(message);
    return;
  }

  const icon = document.getElementById("snackbar-icon");
  if (icon) {
    icon.src = "./img/success-icon.png";
    icon.height = '50';
    icon.width = '40';
  }

  title.innerText = message;
  snackbar.className = "show success";
  setTimeout(function () {
    snackbar.className = snackbar.className.replace("show", "");
  }, 3000);
}
    
export function parseBoolean(booleanString) {

  return (booleanString && String(booleanString).toLowerCase() === 'true');
}    
export function parseBooleanToString(booleanValue){
  return (booleanValue?'Yes':'No');
}

export function findStateFromGstPortal(stcd, states) {
  if (!stcd) {
    return null;
  }
  const stcdLower = String(stcd).toLowerCase();
  return (states || []).find((state) => {
    const stateLabel = state.value.split('(')[0].trim().toLowerCase();
    return state.value.slice(0, 3).toLowerCase() === String(stcd).slice(0, 3).toLowerCase()
      || stcdLower === stateLabel
      || stcdLower.includes(stateLabel)
      || stateLabel.includes(stcdLower);
  });
}

export function mapGstPortalCustomerFields(result) {
  const addr = result?.pradr?.addr || {};
  return {
    trade_name: result?.tradeNam || '',
    legal_name: result?.lgnm || '',
    name: result?.tradeNam || result?.lgnm || '',
    address_1: [addr.bno, addr.st].filter(Boolean).join(', '),
    address_2: addr.loc || '',
    address_3: addr.dst || '',
    pincode: addr.pncd || '',
    gst_status: result?.sts || '',
    registration_type: result?.dty || '',
    stcd: addr.stcd || '',
  };
}

const GST_DETAIL_LABELS = {
  tradeNam: 'Trade Name',
  lgnm: 'Legal Name',
  gstin: 'GSTIN',
  sts: 'GST Status',
  dty: 'Taxpayer Type',
  ctb: 'Constitution of Business',
  rgdt: 'Registration Date',
  cxdt: 'Cancellation Date',
  nba: 'Nature of Business Activities',
  stj: 'State Jurisdiction',
  stjCd: 'State Jurisdiction Code',
  ctj: 'Centre Jurisdiction',
  ctjCd: 'Centre Jurisdiction Code',
  bnm: 'Building Name',
  bno: 'Building Number',
  flno: 'Floor Number',
  st: 'Street',
  loc: 'Locality',
  dst: 'District',
  city: 'City',
  pncd: 'PIN Code',
  stcd: 'State',
  lt: 'Latitude',
  lg: 'Longitude',
};

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const flattenGstDetails = (value, path = '', rows = []) => {
  if (Array.isArray(value)) {
    if (value.length && value.every((item) => typeof item !== 'object')) {
      rows.push([path, value.join(', ')]);
    } else {
      value.forEach((item, index) => flattenGstDetails(item, `${path}[${index + 1}]`, rows));
    }
  } else if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => {
      flattenGstDetails(child, path ? `${path}.${key}` : key, rows);
    });
  } else if (value !== null && value !== undefined && String(value).trim() !== '') {
    rows.push([path, value]);
  }
  return rows;
};

export function confirmGstDetails(gstResult) {
  const rows = flattenGstDetails(gstResult);
  const html = rows.length
    ? `<div style="max-height:420px;overflow:auto;text-align:left;border:1px solid #ddd;border-radius:6px">
        <table style="width:100%;border-collapse:collapse;font-size:14px"><tbody>
          ${rows.map(([path, value]) => {
            const key = path.replace(/\[\d+\]/g, '').split('.').pop();
            const label = GST_DETAIL_LABELS[key] || path
              .replace(/\./g, ' / ')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (letter) => letter.toUpperCase());
            return `<tr>
              <th style="padding:7px;border-bottom:1px solid #eee;vertical-align:top;width:38%">${escapeHtml(label)}</th>
              <td style="padding:7px;border-bottom:1px solid #eee;word-break:break-word">${escapeHtml(value)}</td>
            </tr>`;
          }).join('')}
        </tbody></table>
      </div>`
    : '<p>No GST details were returned.</p>';

  return Swal.fire({
    title: 'GST Details Found',
    html,
    width: 800,
    showCancelButton: true,
    confirmButtonText: 'Accept & Fill',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    focusConfirm: false,
  });
}
